import * as THREE from 'three';
import { WindowConfig, SectionInterface, SectionRect, SectionInsets } from '../types';
import { parseLayoutExpression } from '../layout/parser';
import { resolveLayout } from '../layout/resolver';
import { mirrorLR, flipTB } from '../layout/mirror';
import { createOuterFrame } from '../geometry/FrameGeometry';
import { createDividerMeshes } from '../geometry/DividerGeometry';
import { createSill } from '../geometry/SillGeometry';
import { createSection } from '../sections/SectionFactory';
import { MaterialLibrary } from '../materials/MaterialLibrary';
import { PROFILE } from '../utils/profileConstants';
import { mm } from '../utils/units';

export class WindowGroup {
  readonly group: THREE.Group;
  private sectionInterfaces: Map<number, SectionInterface> = new Map();
  private sectionRects: SectionRect[] = [];
  private groupOffsetX: number = 0;

  constructor() {
    this.group = new THREE.Group();
    this.group.name = 'window-root';
  }

  getSectionInterfaces(): Map<number, SectionInterface> {
    return this.sectionInterfaces;
  }

  getSectionRects(): readonly SectionRect[] {
    return this.sectionRects;
  }

  getGroupOffsetX(): number {
    return this.groupOffsetX;
  }

  rebuild(config: WindowConfig, materialLib: MaterialLibrary): void {
    this.disposeChildren();
    this.sectionInterfaces.clear();

    const totalW = mm(config.width);
    const totalH = mm(config.height);

    // Parse and transform layout
    let ast = parseLayoutExpression(config.layout.expression);
    if (config.layout.mirrorLR) ast = mirrorLR(ast);
    if (config.layout.flipTB) ast = flipTB(ast);

    // Resolve section rectangles within the inner frame area
    const innerX = PROFILE.frameWidth;
    const innerY = PROFILE.frameWidth;
    const innerW = totalW - 2 * PROFILE.frameWidth;
    const innerH = totalH - 2 * PROFILE.frameWidth;

    const rects = resolveLayout(ast, {
      x: innerX,
      y: innerY,
      width: innerW,
      height: innerH,
    });
    this.sectionRects = rects;

    // Frame material
    const frameMat = materialLib.frameMaterial(
      config.frame.material,
      config.frame.outsideColor,
    );

    // Outer frame
    const frame = createOuterFrame(totalW, totalH, PROFILE.frameWidth, PROFILE.frameDepth, frameMat);
    this.group.add(frame);

    // Dividers (mullions and transoms)
    const dividers = createDividerMeshes(
      rects, PROFILE.frameWidth, PROFILE.mullionWidth, PROFILE.mullionDepth, frameMat,
    );
    this.group.add(dividers);

    // Sections
    const innerBounds = { x: innerX, y: innerY, width: innerW, height: innerH };
    for (const rect of rects) {
      const sectionIdx = rect.index - 1;
      const sectionConfig = config.sections[sectionIdx];
      if (!sectionConfig) continue;

      const insets = computeSectionInsets(rect, innerBounds, PROFILE.mullionWidth);
      const section = createSection(sectionConfig, rect, materialLib, frameMat, insets);
      this.sectionInterfaces.set(rect.index, section);
      this.group.add(section.group);
    }

    // Sill
    const sill = createSill(totalW, frameMat);
    this.group.add(sill);

    // Center horizontally, bottom at y=0
    this.groupOffsetX = -totalW / 2;
    this.group.position.set(this.groupOffsetX, 0, 0);
  }

  private disposeChildren(): void {
    while (this.group.children.length > 0) {
      const child = this.group.children[0];
      this.group.remove(child);
      this.disposeObject(child);
    }
  }

  private disposeObject(obj: THREE.Object3D): void {
    if (obj instanceof THREE.Mesh) {
      obj.geometry?.dispose();
      // Don't dispose shared materials from MaterialLibrary
    }
    if ('children' in obj) {
      for (const child of [...obj.children]) {
        this.disposeObject(child);
      }
    }
  }
}

/**
 * Compute per-edge insets for a section rect.
 * Edges touching the outer frame get 0 inset; edges adjacent to a mullion
 * get mullionWidth/2 so the sash sits inside the mullion pocket.
 */
function computeSectionInsets(
  rect: SectionRect,
  innerBounds: { x: number; y: number; width: number; height: number },
  mullionWidth: number,
): SectionInsets {
  const eps = 0.1;
  const half = mullionWidth / 2;
  return {
    left:   Math.abs(rect.x - innerBounds.x) < eps ? 0 : half,
    right:  Math.abs((rect.x + rect.width) - (innerBounds.x + innerBounds.width)) < eps ? 0 : half,
    bottom: Math.abs(rect.y - innerBounds.y) < eps ? 0 : half,
    top:    Math.abs((rect.y + rect.height) - (innerBounds.y + innerBounds.height)) < eps ? 0 : half,
  };
}
